import React, { useEffect, useState } from 'react';
import { UserCircle, Save, Loader2, Lock, X, ShieldCheck } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';
import { usersApi } from '../../lib/api';
import { AgentStoreInfoFields } from './AgentStoreInfoFields';
import { useAuth } from '../../context/AuthContext';

/**
 * Dialog "Il Mio Profilo" — apertura cliccando il blocco utente in sidebar.
 *
 * - Email e Ruolo READ-ONLY (servono privilegi admin).
 * - Modifica: nome, store_info completo, password (opzionale).
 * - Visualizza i dati che vanno nei PDF Lite/Full.
 */
export const AgentProfileDialog = ({ open, onClose }) => {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [draft, setDraft] = useState({ name: '', store_info: {}, password: '' });

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    usersApi.getMyProfile()
      .then((res) => {
        const p = res.data;
        setProfile(p);
        setDraft({
          name: p.name || '',
          store_info: p.store_info || {},
          password: '',
        });
      })
      .catch(() => toast.error('Errore nel caricamento del profilo'))
      .finally(() => setLoading(false));
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: draft.name,
        store_info: draft.store_info,
      };
      if (draft.password) payload.password = draft.password;
      const res = await usersApi.updateMyProfile(payload);
      setProfile(res.data);
      setDraft((d) => ({ ...d, password: '' }));
      toast.success('Profilo aggiornato');
      // Aggiorna l'utente nel context per il refresh del Layout
      if (refreshUser) await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const roleLabel = {
    admin: 'Amministratore',
    store_agent: 'Agente Store',
    logistica: 'Logistica',
    back_office: 'Back Office',
    tecnico: 'Tecnico',
    progettista: 'Progettista',
    installatore: 'Installatore',
    responsabile: 'Responsabile',
    amministrazione: 'Amministrazione',
    viewer: 'Viewer',
  }[user?.role] || user?.role;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0"
        data-testid="agent-profile-dialog"
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-[#0B2A4A] to-[#00BCE7] text-white px-6 py-5">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <UserCircle className="h-5 w-5" />
              Il Mio Profilo
            </DialogTitle>
          </DialogHeader>
          <p className="mt-1 text-sm text-white/85">
            Questi dati appariranno sui preventivi PDF generati dai tuoi preventivi.
          </p>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 py-6">
              <Loader2 className="h-4 w-4 animate-spin" /> Caricamento profilo…
            </div>
          ) : (
            <div className="space-y-5">
              {/* Identità (readonly + name editable) */}
              <section className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-wide font-semibold text-[#0B2A4A]">
                      Nome completo
                    </Label>
                    <Input
                      value={draft.name}
                      onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                      placeholder="Es: Gian Luigi Accattato"
                      className="mt-1.5"
                      data-testid="profile-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wide font-semibold text-[#0B2A4A] flex items-center gap-1">
                      <Lock className="h-3 w-3" /> Email di accesso
                    </Label>
                    <Input
                      value={profile?.email || ''}
                      readOnly
                      className="mt-1.5 bg-slate-50 text-slate-500 cursor-not-allowed"
                      data-testid="profile-email-readonly"
                    />
                    <p className="text-[10px] text-slate-400 mt-1">
                      Solo l&apos;amministratore può cambiare l&apos;email di accesso.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">Ruolo:</span>
                  <Badge variant="outline" className="text-xs">
                    <ShieldCheck className="h-3 w-3 mr-1 text-[#00BCE7]" /> {roleLabel}
                  </Badge>
                </div>
              </section>

              <Separator />

              {/* Dati commerciali */}
              <section>
                <h3 className="text-sm font-bold text-[#0B2A4A] mb-1">
                  Dati commerciali (visibili al cliente)
                </h3>
                <p className="text-xs text-slate-500 mb-3">
                  Questi dati appaiono come &ldquo;Il tuo consulente&rdquo; e &ldquo;Il tuo punto vendita&rdquo; sui PDF del preventivo.
                </p>
                <AgentStoreInfoFields
                  value={draft.store_info}
                  onChange={(v) => setDraft((d) => ({ ...d, store_info: v }))}
                />
              </section>

              <Separator />

              {/* Cambio password */}
              <section>
                <h3 className="text-sm font-bold text-[#0B2A4A] mb-1 flex items-center gap-1.5">
                  <Lock className="h-3.5 w-3.5" /> Cambia password
                </h3>
                <p className="text-xs text-slate-500 mb-3">
                  Opzionale — lascia vuoto per mantenere quella attuale.
                </p>
                <Input
                  type="password"
                  value={draft.password}
                  onChange={(e) => setDraft((d) => ({ ...d, password: e.target.value }))}
                  placeholder="Nuova password (min 6 caratteri)"
                  minLength={6}
                  data-testid="profile-password-input"
                />
              </section>
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="px-6 py-4 bg-slate-50 border-t border-slate-200 gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="cancel-profile-btn">
            <X className="h-4 w-4 mr-2" /> Chiudi
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading || saving}
            className="bg-[#0B2A4A] hover:bg-[#0a253f] text-white min-w-[180px]"
            data-testid="save-profile-btn"
          >
            {saving ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Salvataggio…</>
            ) : (
              <><Save className="h-4 w-4 mr-2" /> Salva modifiche</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
